/**
 * Master key management - the EncryptedMasterKey concern.
 *
 * Owns the password-protected `master_key` backup: sealing it (AES-256-GCM keyed
 * by the password key, PBKDF2 of the user password), unlocking it (resolve from
 * the preserved password key, or recover by re-deriving from the user's password),
 * decoding/validating the raw `master_key`, and the per-device store accessors.
 *
 * The PBKDF2 salt is packed inside the blob (`salt || iv || ciphertext+tag`), so
 * the cloud/local backup is just `{ blob, hmac }`. PBKDF2 runs only at provision
 * and password-recovery - never on every resolve.
 */

import { get } from 'svelte/store';
import { app, os, user_data } from 'src/stores';
import { getCameraStorageKeys } from 'src/services/cam-cloud/recordings';
import { camera_master_key_ios } from 'src/services/native_container_requests_ios';
import { camera_master_key_android } from 'src/services/native_container_requests_android';
import {
	camera_storage_keys,
	pickBackupForDate,
	type EncryptedMasterKey,
} from 'src/components/Camera/stores/camera_storage_keys';
import { camera_storage_passphrases } from 'src/components/Camera/stores/camera_storage_passphrases';
import { camera_storage_resolved_keys } from 'src/components/Camera/stores/camera_storage_resolved_keys';
import type { SealedMasterKey } from './types/master-key';
import { base64ToBytes, bytesToBase64, bytesToHex, hexToBytes } from './encoding';

/** AES-GCM nonce length in bytes. */
const GCM_IV_BYTES = 12;

/** Length in bytes of the random PBKDF2 salt packed into every blob. */
const SALT_BYTES = 16;

/** Byte length of the camera's `master_key` secret. */
const K_MASTER_LENGTH = 32;

/**
 * PBKDF2 iteration count used to derive the backup key (OWASP 2023 recommendation
 * for PBKDF2-HMAC-SHA256). Fixed constant - not persisted; a version prefix can
 * be added later if it ever needs to change. Exported for dev diagnostics only.
 */
export const PBKDF2_ITERATIONS = 600_000;

/**
 * Decodes the base64 `master_key` secret into its 32 raw bytes.
 *
 * @param masterKey - The stored recording secret (base64 `master_key`).
 * @returns The 32 key bytes, or `null` if the secret is empty or invalid.
 */
export const masterKeyToBytes = (masterKey: string): Uint8Array | null => {
	if (!masterKey) return null;
	try {
		const decoded = base64ToBytes(masterKey);
		return decoded.length === K_MASTER_LENGTH ? decoded : null;
	} catch {
		return null;
	}
};

/**
 * Derives the 32-byte password key (AES-GCM + HMAC key for the `master_key`
 * backup) from the user password via PBKDF2-HMAC-SHA256.
 */
const derivePasswordKey = async (
	password: string,
	salt: Uint8Array,
	iterations: number,
): Promise<Uint8Array> => {
	const baseKey = await crypto.subtle.importKey(
		'raw',
		new TextEncoder().encode(password) as BufferSource,
		'PBKDF2',
		false,
		['deriveBits'],
	);
	const bits = await crypto.subtle.deriveBits(
		{ name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
		baseKey,
		256,
	);
	return new Uint8Array(bits);
};

/** Imports the 32 password-key bytes as a non-extractable AES-256-GCM key. */
const importAesKey = (passwordKey: Uint8Array): Promise<CryptoKey> => {
	return crypto.subtle.importKey('raw', passwordKey as BufferSource, 'AES-GCM', false, [
		'encrypt',
		'decrypt',
	]);
};

/** Imports the 32 password-key bytes as a non-extractable HMAC-SHA256 key. */
const importHmacKey = (passwordKey: Uint8Array): Promise<CryptoKey> => {
	return crypto.subtle.importKey(
		'raw',
		passwordKey as BufferSource,
		{ name: 'HMAC', hash: 'SHA-256' },
		false,
		['sign', 'verify'],
	);
};

/**
 * Packs the backup blob as `base64(salt || iv || ciphertext+gcmTag)` - the only
 * place the blob layout is defined.
 */
const packBlob = (salt: Uint8Array, iv: Uint8Array, ciphertext: Uint8Array): string => {
	const combined = new Uint8Array(salt.length + iv.length + ciphertext.length);
	combined.set(salt, 0);
	combined.set(iv, salt.length);
	combined.set(ciphertext, salt.length + iv.length);
	return bytesToBase64(combined);
};

/** Splits a packed blob back into its salt / iv / ciphertext parts. */
const unpackBlob = (blob: string): { salt: Uint8Array; iv: Uint8Array; ciphertext: Uint8Array } => {
	const combined = base64ToBytes(blob);
	return {
		salt: combined.slice(0, SALT_BYTES),
		iv: combined.slice(SALT_BYTES, SALT_BYTES + GCM_IV_BYTES),
		ciphertext: combined.slice(SALT_BYTES + GCM_IV_BYTES),
	};
};

/** Computes the base64 HMAC-SHA256 of the plaintext `master_key`, keyed by the password key. */
const hmacMasterKey = async (masterKey: string, passwordKey: Uint8Array): Promise<string> => {
	const key = await importHmacKey(passwordKey);
	const data = new TextEncoder().encode(masterKey);
	const signature = await crypto.subtle.sign('HMAC', key, data as BufferSource);
	return bytesToBase64(new Uint8Array(signature));
};

/** Verifies the base64 HMAC-SHA256 of a decrypted `master_key` against the stored value. */
const verifyMasterKeyHmac = async (
	masterKey: string,
	passwordKey: Uint8Array,
	hmac: string,
): Promise<boolean> => {
	const key = await importHmacKey(passwordKey);
	const data = new TextEncoder().encode(masterKey);
	try {
		return await crypto.subtle.verify(
			'HMAC',
			key,
			base64ToBytes(hmac) as BufferSource,
			data as BufferSource,
		);
	} catch {
		return false;
	}
};

/**
 * Encrypts `master_key` with AES-256-GCM keyed by a fresh password key (PBKDF2)
 * and returns the packed backup blob, its HMAC, and the password-key hex.
 *
 * @param masterKey - The base64 `master_key` secret to back up.
 * @param password - The raw user password.
 * @returns The sealed backup ({@link SealedMasterKey}).
 */
export const sealMasterKey = async (masterKey: string, password: string): Promise<SealedMasterKey> => {
	const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
	const passwordKey = await derivePasswordKey(password, salt, PBKDF2_ITERATIONS);
	const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_BYTES));
	const key = await importAesKey(passwordKey);
	const ciphertext = new Uint8Array(
		await crypto.subtle.encrypt(
			{ name: 'AES-GCM', iv: iv as BufferSource },
			key,
			new TextEncoder().encode(masterKey) as BufferSource,
		),
	);

	const blob = packBlob(salt, iv, ciphertext);
	const hmac = await hmacMasterKey(masterKey, passwordKey);
	return { blob, hmac, passwordKeyHex: bytesToHex(passwordKey) };
};

/** Decrypts a packed backup blob with the given password key. Throws on tag mismatch. */
const openMasterKey = async (blob: string, passwordKey: Uint8Array): Promise<string> => {
	const { iv, ciphertext } = unpackBlob(blob);
	const key = await importAesKey(passwordKey);
	const plaintext = await crypto.subtle.decrypt(
		{ name: 'AES-GCM', iv: iv as BufferSource },
		key,
		ciphertext as BufferSource,
	);
	return new TextDecoder().decode(plaintext);
};

/**
 * Reads the ENCRYPTED `master_key` backup persisted for a device.
 *
 * @param deviceId - The device id the backup is stored against.
 * @returns The encrypted backup, or `null` when none is saved.
 */
export const getEncryptedMasterKey = (deviceId: string): EncryptedMasterKey | null => {
	return camera_storage_keys.getEncrypted(deviceId);
};

/**
 * Persists a device's ENCRYPTED `master_key` backup.
 *
 * @param deviceId - The device id to store the backup against.
 * @param key - The encrypted backup (`{ blob, hmac }`).
 */
export const setEncryptedMasterKey = (deviceId: string, key: EncryptedMasterKey): void => {
	camera_storage_keys.setEncrypted(deviceId, key);
};

/**
 * Reads the in-memory cache of a resolved `master_key` for a device's era. When
 * the exact era matters, prefer resolving by date via {@link resolveMasterKey}.
 *
 * @param deviceId - The device id the key was resolved against.
 * @param era - The era (`createdAt`) whose cached `master_key` to read.
 * @returns The base64 `master_key`, or `null` when not resolved.
 */
export const getResolvedMasterKey = (deviceId: string, era: string): string | null => {
	return camera_storage_resolved_keys.getKey(deviceId, era);
};

/**
 * Whether the password key for the era active at `date` (newest when omitted) is
 * preserved for the device — i.e. that era can be decrypted without a prompt.
 * Used by reactive consumers (player/thumbnails) to re-init exactly when the
 * recording's era is unlocked, and to decide whether to prompt.
 *
 * @param deviceId - The device id (normalised MAC).
 * @param date - Optional recording ISO `date` selecting the era (newest when omitted).
 * @returns `true` when the era's password key is preserved locally.
 */
export const hasPreservedEraKey = (deviceId: string, date?: string): boolean => {
	if (!deviceId) return false;
	const backup = camera_storage_keys.getForDate(deviceId, date);
	return !!(backup && camera_storage_passphrases.getPasswordKey(deviceId, backup.createdAt));
};

/**
 * Clears every trace of a device's master key: encrypted backups, resolved keys,
 * and password keys across all eras. Used for a full reset (e.g. re-provisioning).
 *
 * @param deviceId - The device id whose key state should be wiped.
 */
export const clearStoredMasterKey = (deviceId: string): void => {
	camera_storage_keys.remove(deviceId);
	camera_storage_resolved_keys.remove(deviceId);
	camera_storage_passphrases.remove(deviceId);
};

/** Reports a failed native master-key handoff without surfacing it to the caller. */
const logNativeHandoffFailure = (error: unknown): void => {
	console.warn('[master-key-management] failed to hand master key to native', error);
};

/**
 * Hands a device's plaintext `master_key` to the native app's secure storage,
 * mapped by account email + device id, so push notifications can derive the
 * recording key and decrypt their preview images.
 *
 * @remarks
 * Native-only: a no-op in the browser, where there is no bridge to call. The
 * call is fire-and-forget and never throws — a native container without the
 * handler must not break provisioning or unlocking. Only the newest era's key
 * is worth sending, since notifications always reference the current key.
 *
 * @param deviceId - The device id (normalised MAC) the key belongs to.
 * @param masterKey - The base64 `master_key` to store natively.
 */
export const syncMasterKeyToNative = (deviceId: string, masterKey: string): void => {
	if (!deviceId || !masterKey || !get(app)) return;

	const email = user_data.getEmail();
	if (!email) return;

	if (get(os) === 'IOS') {
		camera_master_key_ios(email, deviceId, masterKey);
		return;
	}

	// Only the Android bridge is promise-based, so it is the one that can reject.
	camera_master_key_android(email, deviceId, masterKey).catch(logNativeHandoffFailure);
};

/** Reads the device's backup history from the local store, else fetches + caches it from the cloud. */
const getBackups = async (deviceId: string): Promise<EncryptedMasterKey[]> => {
	const local = camera_storage_keys.getAll(deviceId);
	if (local.length) return local;

	try {
		const history = await getCameraStorageKeys(deviceId);
		if (history.length) {
			camera_storage_keys.setAll(deviceId, history);
			return history;
		}
	} catch (error) {
		console.warn('[master-key-management] failed to fetch cloud key backups', error);
	}
	return [];
};

/**
 * Attempts to decrypt + HMAC-verify a single backup with a password key. Returns
 * the plaintext `master_key` on success, else `null` (wrong key / corrupt blob).
 */
const openBackup = async (
	backup: EncryptedMasterKey,
	passwordKey: Uint8Array,
): Promise<string | null> => {
	let masterKey: string;
	try {
		masterKey = await openMasterKey(backup.blob, passwordKey);
	} catch {
		return null;
	}
	const hmacValid = await verifyMasterKeyHmac(masterKey, passwordKey, backup.hmac);
	if (!hmacValid) return null;
	if (!masterKeyToBytes(masterKey)) return null;
	return masterKey;
};

/**
 * Resolves the `master_key` for the era active at a recording's `date` and
 * decrypts it with the era's preserved password key (AES-GCM, no PBKDF2).
 * Reads/writes the era's in-memory resolved slot. Primary playback path.
 *
 * Omit `date` to resolve the **newest** era — the device-level lookup used by
 * live view, thumbnails, and latest previews.
 *
 * @param deviceId - The device id (normalised MAC) the key belongs to.
 * @param date - The recording's ISO `date` used to pick the era (newest when omitted).
 * @returns The base64 `master_key` for that era, or `null` when the era can't be
 * decrypted (caller prompts).
 */
export const resolveMasterKey = async (deviceId: string, date?: string): Promise<string | null> => {
	if (!deviceId) return null;

	const backups = await getBackups(deviceId);
	if (!backups.length) return null;

	const backup = camera_storage_keys.getForDate(deviceId, date);
	if (!backup) return null;

	// Reuse the era's cached plaintext when available (skips the AES-GCM decrypt).
	const cached = camera_storage_resolved_keys.getKey(deviceId, backup.createdAt);
	if (cached) return cached;

	const passwordKeyHex = camera_storage_passphrases.getPasswordKey(deviceId, backup.createdAt);
	if (!passwordKeyHex) return null;
	const passwordKey = hexToBytes(passwordKeyHex);
	if (!passwordKey) return null;

	const masterKey = await openBackup(backup, passwordKey);
	if (!masterKey) return null;

	camera_storage_resolved_keys.setKey(deviceId, backup.createdAt, masterKey);
	return masterKey;
};

/**
 * Recovers a device's `master_key` for a single era from a user-typed password:
 * the era active at `date`, or the newest era when `date` is omitted. Only that
 * era's salt is used for PBKDF2, and only its password key + `master_key` are
 * preserved, so a prompt unlocks exactly the era it was opened for and no other
 * era's state is touched. Unlocking the newest era also hands its `master_key` to
 * native secure storage (see {@link syncMasterKeyToNative}).
 *
 * @remarks
 * A wrong password decrypts nothing and preserves nothing, so it is a no-op and
 * any previously-unlocked era survives; the prompt surfaces a wrong-password
 * error. Eras that happen to share a password are unlocked independently, each
 * the first time one of its recordings is opened.
 *
 * @param deviceId - The device id (normalised MAC) the key belongs to.
 * @param password - The raw user password.
 * @param date - Optional recording date selecting the era (newest when omitted).
 * @returns The recovered base64 `master_key` for that era, or `null` when no
 * backup is available or the password does not decrypt that era.
 */
export const recoverMasterKey = async (
	deviceId: string,
	password: string,
	date?: string,
): Promise<string | null> => {
	if (!deviceId || !password) return null;

	const backups = await getBackups(deviceId);
	const target = pickBackupForDate(backups, date);
	if (!target) return null;

	const { salt } = unpackBlob(target.blob);
	const passwordKey = await derivePasswordKey(password, salt, PBKDF2_ITERATIONS);
	const masterKey = await openBackup(target, passwordKey);
	if (!masterKey) return null;

	camera_storage_passphrases.setPasswordKey(deviceId, bytesToHex(passwordKey), target.createdAt);
	camera_storage_resolved_keys.setKey(deviceId, target.createdAt, masterKey);

	// Unlocking the latest master key syncs it with the native apps.
	if (target === backups?.[0]) {
		syncMasterKeyToNative(deviceId, masterKey);
	}

	return masterKey;
};
